// AGC: Activity Feed Service
// AGC: Fetches unified activity feed from backend

import { getBackendUrl } from '../utils/backendUrl'

const BASE_URL = getBackendUrl()

// =============================================================================
// AGC: TYPES
// =============================================================================

export type ActivityType = 
  | 'task_created' 
  | 'task_completed' 
  | 'stock_entry' 
  | 'stock_exit' 
  | 'expense_added'
  | 'lot_created'
  | 'lot_archived'

export interface Activity {
  id: string
  type: ActivityType
  title: string
  description: string
  timestamp: string | Date
  icon: string
  color: string
  metadata?: Record<string, any>
}

export interface ActivityFeedFilters {
  type?: ActivityType
  limit?: number
  startDate?: Date
  endDate?: Date
}

// =============================================================================
// AGC: API FUNCTIONS
// =============================================================================

/**
 * AGC: Fetch recent activities from backend
 */
export async function fetchRecentActivities(
  authToken: string,
  filters?: ActivityFeedFilters
): Promise<Activity[]> {
  try {
    const params = new URLSearchParams()
    if (filters?.type) params.append('type', filters.type)
    if (filters?.limit) params.append('limit', filters.limit.toString())

    const url = `${BASE_URL}/api/v1/activities/recent${params.toString() ? `?${params.toString()}` : ''}`
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      if (response.status === 401) {
        console.warn('AGC: Unauthorized - user not logged in')
        return []
      }
      throw new Error(`Failed to fetch activities: ${response.statusText}`)
    }

    const data = await response.json()
    return data.success ? (data.data || []) : []
  } catch (error) {
    console.error('AGC: Error fetching activities:', error)
    return []
  }
}

// =============================================================================
// AGC: UTILITY FUNCTIONS
// =============================================================================

/**
 * AGC: Format activity timestamp for display
 */
export function formatActivityTime(timestamp: string | Date): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Agora mesmo'
  if (diffMins < 60) return `${diffMins} min atrás`
  if (diffHours < 24) return `${diffHours}h atrás`
  if (diffDays < 7) return `${diffDays}d atrás`
  
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
}

/**
 * AGC: Get icon for activity type
 */
export function getActivityIcon(type: ActivityType): string {
  const icons: Record<ActivityType, string> = {
    task_created: '📋',
    task_completed: '✅',
    stock_entry: '📦',
    stock_exit: '📤',
    expense_added: '💰',
    lot_created: '🌱',
    lot_archived: '📁',
  }
  return icons[type] || '📝'
}

/**
 * AGC: Get color for activity type
 */
export function getActivityColor(type: ActivityType): string {
  const colors: Record<ActivityType, string> = {
    task_created: '#3B82F6',
    task_completed: '#22C55E',
    stock_entry: '#8B5CF6',
    stock_exit: '#F59E0B',
    expense_added: '#EF4444',
    lot_created: '#10B981',
    lot_archived: '#6B7280',
  }
  return colors[type] || '#6B7280'
}

/**
 * AGC: Group activities by date
 */
export function groupActivitiesByDate(activities: Activity[]): Map<string, Activity[]> {
  const grouped = new Map<string, Activity[]>()
  
  for (const activity of activities) {
    const date = new Date(activity.timestamp)
    const dateKey = date.toLocaleDateString('pt-BR', { 
      weekday: 'long',
      day: '2-digit', 
      month: 'long' 
    })
    
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, [])
    }
    grouped.get(dateKey)!.push(activity)
  }
  
  return grouped
}

export default {
  fetchRecentActivities,
  formatActivityTime,
  getActivityIcon,
  getActivityColor,
  groupActivitiesByDate,
}

